import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Activity, Archive, CalendarDays, Download, Eye, FilePenLine, Gamepad2, GraduationCap, KeyRound, ListChecks, LockKeyhole, Send, TicketCheck, Trash2, UserPlus } from "lucide-react";
import { createVersionSnapshot } from "../shared/domain/index.js";
import type { EvaluationSummary, TeacherWorkspaceSnapshot } from "../shared/domain/index.js";
import { WebsiteModal } from "./WebsiteOverlay.js";

interface TeacherEvaluationNotice {
  attemptId: string;
  ticketNumber: string;
  ticketTitle: string;
  studentName: string;
  summary: EvaluationSummary;
}

type TeacherGroupListItem = {
  id: string;
  name: string;
  joinCode: string;
  status: "active" | "archived" | "deleted";
  studentCount: number;
  activePublicationCount: number;
  publicationCount: number;
  attemptCount: number;
  completedAttemptCount: number;
  totalTargets: number;
  completedTargets: number;
  progressPercent: number;
  createdAt: string;
  updatedAt: string;
};

type TeacherDashboardPeriod = "today" | "week" | "month";

type TeacherGroupMember = {
  studentId: string;
  studentName: string;
  email: string;
  lastActivity: string | null;
  attemptCount: number;
  openAttemptCount: number;
  completedTargets: number;
  totalTargets: number;
  averageScore: number;
};

type TeacherGroupRankingRow = {
  studentId: string;
  studentName: string;
  solvedTotal: number;
  attemptCount: number;
  averageScore: number;
  lastActivity: string | null;
};

type TeacherGroupTeacher = {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
};

type TeacherGroupAttemptDetail = {
  attemptId: string;
  studentId: string;
  studentName: string;
  ticketNumber: string;
  ticketTitle: string;
  status: string;
  progressPercent: number;
  completedTargets: number;
  totalTargets: number;
  lastActivity: string | null;
};

type TeacherGroupPublication = {
  id: string;
  ticketVersionId: string;
  ticketTitle: string;
  status: string;
  availableFrom: string;
  availableUntil: string | null;
  attemptCount: number;
  completedAttemptCount: number;
};

type TeacherGroupDetail = {
  group: TeacherGroupListItem;
  teachers: TeacherGroupTeacher[];
  availableTeachers: TeacherGroupTeacher[];
  members: TeacherGroupMember[];
  ranking: TeacherGroupRankingRow[];
  attempts: TeacherGroupAttemptDetail[];
  closedAttempts: TeacherGroupAttemptDetail[];
  publications: TeacherGroupPublication[];
  memberPage: number;
  memberPageSize: number;
  memberTotal: number;
  period: string;
};

type TeacherStudentListItem = {
  studentId: string;
  name: string;
  email: string;
  className: string | null;
  openCount: number;
  completedCount: number;
  reviewCount: number;
};

type TeacherStudentDetail = {
  student: {
    studentId: string;
    name: string;
    email: string;
  };
  attempts: Array<{
    attemptId: string;
    status: string;
    submittedAt: string | null;
    ticketNumber: string;
    ticketTitle: string;
  }>;
};

type TeacherScenarioInstanceDetail = {
  attempt: {
    attemptId: string;
    status: string;
    submittedAt: string | null;
    report: string | null;
    simulatorState: unknown;
    targetResults: unknown;
    student: {
      studentId: string;
      name: string;
      email: string;
    };
    ticket: {
      number: string;
      title: string;
    };
  };
  feedbackHistory: Array<{
    id: string;
    bodyText: string;
    score: number | null;
    createdAt: string;
    teacher: {
      userId: string;
      name: string;
      email: string;
    };
  }>;
};

type TeacherAssignmentOptionGroup = {
  id: string;
  name: string;
  code: string | null;
  studentCount: number;
};

type TeacherAssignmentTicketVersion = {
  id: string;
  templateId: string;
  versionNumber: number;
  title: string;
  topic: string;
  complexity: string;
};

type TeacherTicketBatchListItem = {
  id: string;
  classGroupId: string | null;
  className: string | null;
  status: string;
  scheduledFor: string | null;
  dueAt: string | null;
  publicationCount: number;
};

type TeacherCalendarSnapshot = {
  publications: Array<{ id: string; classGroupId: string | null; ticketVersionId: string | null; availableFrom: string; availableUntil: string | null; status: string }>;
  batches: Array<{ id: string; classGroupId: string | null; scheduledFor: string | null; dueAt: string | null; status: string }>;
};

const evaluatorOptions = [
  { value: "network.client_dns", label: "Client DNS ingesteld" },
  { value: "network.dhcp_scope_exists", label: "DHCP scope bestaat" },
  { value: "network.dns_record_exists", label: "DNS-record bestaat" },
  { value: "identity.domain_user_exists", label: "Domeingebruiker bestaat" },
  { value: "identity.domain_group_membership", label: "Domeingroep membership" },
  { value: "identity.domain_controller_promoted", label: "Domain controller promotie" },
  { value: "identity.domain_login_attempted_succeeded", label: "Domeinlogin gelukt" },
  { value: "policy.gpo_exists", label: "GPO bestaat" },
  { value: "file.folder_exists", label: "Map bestaat" },
  { value: "file.file_exists", label: "Bestand bestaat" },
  { value: "file.file_content_matches", label: "Bestandsinhoud klopt" },
  { value: "file.share_exists", label: "Share bestaat" },
  { value: "file.acl_principal_has_permission", label: "ACL/recht klopt" },
  { value: "service.status_matches", label: "Servicestatus klopt" },
  { value: "firewall.rule_exists", label: "Firewallregel bestaat" },
  { value: "audit.event_exists", label: "Audit-event bestaat" },
  { value: "manual.review", label: "Handmatige beoordeling" }
] as const;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "FD";
}

export function TeacherWorkspace({ snapshot, onRefresh }: { snapshot: TeacherWorkspaceSnapshot; onRefresh: () => Promise<void> | void }) {
  const { activePublication, classes, liveBoardRows, liveBoardSession, template, tickets } = snapshot;
  const [evaluatingAttemptId, setEvaluatingAttemptId] = useState<string | null>(null);
  const [evaluationNotice, setEvaluationNotice] = useState<TeacherEvaluationNotice | null>(null);
  const [evaluationError, setEvaluationError] = useState("");
  const [selectedClassId, setSelectedClassId] = useState<string | null>(classes[0]?.classGroupId ?? null);
  const [teacherGroups, setTeacherGroups] = useState<TeacherGroupListItem[]>([]);
  const [teacherGroupSearch, setTeacherGroupSearch] = useState("");
  const [teacherGroupStatus, setTeacherGroupStatus] = useState<"active" | "archived" | "all">("active");
  const [teacherGroupSort, setTeacherGroupSort] = useState("progress");
  const [teacherDashboardPeriod, setTeacherDashboardPeriod] = useState<TeacherDashboardPeriod>("today");
  const initialGroupRoute = typeof window === "undefined" ? "" : (window.location.pathname.match(/^\/teacher\/groups\/([^/]+)/)?.[1] ?? "");
  const [selectedTeacherGroupId, setSelectedTeacherGroupId] = useState<string>(initialGroupRoute ? decodeURIComponent(initialGroupRoute) : "");
  const [selectedTeacherGroup, setSelectedTeacherGroup] = useState<TeacherGroupDetail | null>(null);
  const [teacherGroupMemberPage, setTeacherGroupMemberPage] = useState(1);
  const [selectedGroupStudentId, setSelectedGroupStudentId] = useState<string>("");
  const [newGroupName, setNewGroupName] = useState("");
  const [groupRenameDraft, setGroupRenameDraft] = useState("");
  const [teacherToAdd, setTeacherToAdd] = useState("");
  const [teacherGroupBusy, setTeacherGroupBusy] = useState(false);
  const [teacherGroupMessage, setTeacherGroupMessage] = useState("");
  const [teacherStudents, setTeacherStudents] = useState<TeacherStudentListItem[]>([]);
  const [teacherStudentsTotal, setTeacherStudentsTotal] = useState(0);
  const [teacherStudentsLoading, setTeacherStudentsLoading] = useState(false);
  const [teacherStudentsError, setTeacherStudentsError] = useState("");
  const [teacherStudentsQuery, setTeacherStudentsQuery] = useState("");
  const [teacherStudentsHasOpen, setTeacherStudentsHasOpen] = useState<"all" | "true" | "false">("all");
  const [teacherStudentsSortBy, setTeacherStudentsSortBy] = useState<"name" | "open" | "completed" | "review">("name");
  const [teacherStudentsSortDir, setTeacherStudentsSortDir] = useState<"asc" | "desc">("asc");
  const [teacherStudentsPage, setTeacherStudentsPage] = useState(1);
  const [selectedTeacherStudentId, setSelectedTeacherStudentId] = useState("");
  const [selectedTeacherStudentDetail, setSelectedTeacherStudentDetail] = useState<TeacherStudentDetail | null>(null);
  const [teacherSettingsLoading, setTeacherSettingsLoading] = useState(false);
  const [teacherSettingsMessage, setTeacherSettingsMessage] = useState("");
  const [teacherMaxActiveTickets, setTeacherMaxActiveTickets] = useState("25");
  const [teacherAcceptsNewTickets, setTeacherAcceptsNewTickets] = useState(true);
  const [teacherTimezone, setTeacherTimezone] = useState("Europe/Amsterdam");
  const [teacherAvailabilityWindows, setTeacherAvailabilityWindows] = useState<Array<{ dayOfWeek: number; startTime: string; endTime: string }>>([]);
  const [teacherAvailabilityExceptions, setTeacherAvailabilityExceptions] = useState<Array<{ date: string; reason: string; blocked: boolean }>>([]);
  const [assignmentGroups, setAssignmentGroups] = useState<TeacherAssignmentOptionGroup[]>([]);
  const [assignmentVersions, setAssignmentVersions] = useState<TeacherAssignmentTicketVersion[]>([]);
  const [assignmentClassId, setAssignmentClassId] = useState(classes[0]?.classGroupId ?? "");
  const [assignmentVersionId, setAssignmentVersionId] = useState(activePublication.ticketVersionId);
  const [assignmentDueAt, setAssignmentDueAt] = useState("");
  const [assignmentScheduledFor, setAssignmentScheduledFor] = useState("");
  const [assignmentStoryContext, setAssignmentStoryContext] = useState("Ticket voor {student} in de servicedeskplanning.");
  const [assignmentMessage, setAssignmentMessage] = useState("");
  const [assignmentBusy, setAssignmentBusy] = useState(false);
  const [ticketBatches, setTicketBatches] = useState<TeacherTicketBatchListItem[]>([]);
  const [teacherCalendar, setTeacherCalendar] = useState<TeacherCalendarSnapshot>({ publications: [], batches: [] });
  const [scenarioReview, setScenarioReview] = useState<TeacherScenarioInstanceDetail | null>(null);
  const [scenarioReviewLoading, setScenarioReviewLoading] = useState(false);
  const [scenarioReviewError, setScenarioReviewError] = useState("");
  const [scenarioFeedbackText, setScenarioFeedbackText] = useState("");
  const [scenarioFeedbackScore, setScenarioFeedbackScore] = useState("");
  const [scenarioFeedbackSaving, setScenarioFeedbackSaving] = useState(false);
  const [scenarioFeedbackMessage, setScenarioFeedbackMessage] = useState("");
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderStep, setBuilderStep] = useState<1 | 2 | 3>(1);
  const [builderError, setBuilderError] = useState("");
  const [builderBusy, setBuilderBusy] = useState(false);
  const [newTemplateId, setNewTemplateId] = useState<string | null>(null);
  const [newVersionId, setNewVersionId] = useState<string | null>(null);
  const [templateDraft, setTemplateDraft] = useState({
    title: "",
    description: "",
    topic: "networking",
    complexity: "intermediate",
    category: "game",
    environment: "hybrid",
    skills: "",
    learningObjectives: ""
  });
  const [targetsDraft, setTargetsDraft] = useState<Array<{
    id: string;
    title: string;
    safeTitle: string;
    description: string;
    visibility: "visible" | "hidden" | "hidden_until_completed";
    evaluatorKey: string;
    weight: string;
    scoring: "automatic" | "manual" | "hybrid";
    expected: string;
    teacherNotes: string;
  }>>([{
    id: "target-1",
    title: "",
    safeTitle: "",
    description: "",
    visibility: "visible",
    evaluatorKey: "network.client_dns",
    weight: "1",
    scoring: "automatic",
    expected: "{}",
    teacherNotes: ""
  }]);
  const [publicationDraft, setPublicationDraft] = useState({
    classGroupId: classes[0]?.classGroupId ?? "",
    availableFrom: new Date().toISOString().slice(0, 16),
    availableUntil: "",
    attemptLimit: "1",
    feedbackMode: "teacher_released"
  });
  const topicOptions = ["identity", "networking", "filesystem", "security", "server", "m365_entra", "troubleshooting", "operations"] as const;
  const complexityOptions = ["intro", "basic", "intermediate", "advanced", "exam"] as const;
  const environmentOptions = ["hybrid", "onprem", "cloud"] as const;
  const versionPreview = useMemo(() => createVersionSnapshot({
    template,
    versionNumber: 3,
    targets: tickets[0]?.targets ?? [],
    startCriteria: { machineProfile: "hybrid-domain", brokenDns: true },
    assessment: { automaticWeight: 0.75, manualReportWeight: 0.25 },
    publishedByUserId: "teacher-a-kazan",
    publishedAt: "2026-04-21T09:00:00.000Z"
  }), [template, tickets]);
  const uniqueStudents = new Set(liveBoardRows.map((row) => row.studentName)).size;
  const activeAttemptCount = liveBoardRows.filter((row) => row.status === "Bezig" || row.status === "Ingeleverd").length;
  const teacherPeriodOptions: Array<{ value: TeacherDashboardPeriod; label: string; multiplier: number }> = [
    { value: "today", label: "Vandaag", multiplier: 1 },
    { value: "week", label: "Deze week", multiplier: 4 },
    { value: "month", label: "Maand", multiplier: 12 }
  ];
  const selectedTeacherPeriod = teacherPeriodOptions.find((option) => option.value === teacherDashboardPeriod) ?? teacherPeriodOptions[0];
  const totalTargetsInProgress = liveBoardRows.reduce((sum, row) => sum + row.totalTargets, 0);
  const completedTargetsInProgress = liveBoardRows.reduce((sum, row) => sum + row.completedTargets, 0);
  const classProgressPercent = totalTargetsInProgress > 0
    ? Math.round((completedTargetsInProgress / totalTargetsInProgress) * 100)
    : 0;
  const activePercent = liveBoardRows.length > 0
    ? Math.round((activeAttemptCount / liveBoardRows.length) * 100)
    : 0;
  const reviewPercent = liveBoardRows.length > 0
    ? Math.round((liveBoardRows.filter((row) => row.status === "Ingeleverd").length / liveBoardRows.length) * 100)
    : 0;
  const ticketCoveragePercent = Math.min(100, Math.max(0, versionPreview.targetsSnapshot.length * 20));
  const periodDeltaLabel = selectedTeacherPeriod.value === "today"
    ? "+ live"
    : `+${Math.max(0, Math.round(liveBoardRows.length * selectedTeacherPeriod.multiplier - liveBoardRows.length))} ${selectedTeacherPeriod.label.toLowerCase()}`;
  const teacherAvatarStudents = Array.from(new Set(liveBoardRows.map((row) => row.studentName).filter(Boolean))).slice(0, 4);
  const teacherAvatarOverflow = Math.max(0, uniqueStudents - teacherAvatarStudents.length);
  const selectedClass = classes.find((classView) => classView.classGroupId === selectedClassId) ?? classes[0] ?? null;
  const selectedGroupStudent = selectedTeacherGroup?.members.find((member) => member.studentId === selectedGroupStudentId)
    ?? selectedTeacherGroup?.members[0]
    ?? null;
  const selectedGroupStudentAttempts = selectedGroupStudent && selectedTeacherGroup
    ? selectedTeacherGroup.attempts.filter((attempt) => attempt.studentId === selectedGroupStudent.studentId)
    : [];

  async function loadTeacherGroups() {
    const params = new URLSearchParams();
    if (teacherGroupSearch.trim()) params.set("q", teacherGroupSearch.trim());
    if (teacherGroupStatus !== "all") params.set("status", teacherGroupStatus);
    params.set("sortBy", teacherGroupSort);
    const response = await fetch(`/api/v2/teacher/groups?${params.toString()}`, { credentials: "include" });
    if (!response.ok) throw new Error(`teacher_groups_failed_${response.status}`);
    const data = await response.json() as { groups?: TeacherGroupListItem[] };
    const groups = data.groups ?? [];
    setTeacherGroups(groups);
    if (!selectedTeacherGroupId && groups[0]) {
      setSelectedTeacherGroupId(groups[0].id);
    }
  }

  async function loadTeacherGroupDetail(groupId: string, period = selectedTeacherGroup?.period ?? "total", memberPage = teacherGroupMemberPage) {
    if (!groupId) return;
    const params = new URLSearchParams({
      period,
      limit: "20",
      memberPage: String(memberPage),
      memberPageSize: "20"
    });
    const response = await fetch(`/api/v2/teacher/groups/${encodeURIComponent(groupId)}?${params.toString()}`, {
      credentials: "include"
    });
    if (!response.ok) throw new Error(`teacher_group_detail_failed_${response.status}`);
    const detail = await response.json() as TeacherGroupDetail;
    setSelectedTeacherGroup(detail);
    setGroupRenameDraft(detail.group.name);
    setTeacherToAdd("");
    setSelectedGroupStudentId((current) => detail.members.some((member) => member.studentId === current) ? current : detail.members[0]?.studentId ?? "");
  }

  useEffect(() => {
    let canceled = false;
    async function run() {
      try {
        await loadTeacherGroups();
        if (!canceled) setTeacherGroupMessage("");
      } catch {
        if (!canceled) setTeacherGroupMessage("Groepen laden is mislukt.");
      }
    }
    void run();
    return () => {
      canceled = true;
    };
  }, [teacherGroupSearch, teacherGroupStatus, teacherGroupSort]);

  useEffect(() => {
    setTeacherGroupMemberPage(1);
    if (!selectedTeacherGroupId || typeof window === "undefined") return;
    const targetPath = `/teacher/groups/${encodeURIComponent(selectedTeacherGroupId)}`;
    if (window.location.pathname !== targetPath) {
      window.history.replaceState(null, "", targetPath);
    }
  }, [selectedTeacherGroupId]);

  useEffect(() => {
    let canceled = false;
    async function run() {
      try {
        if (!selectedTeacherGroupId) return;
        await loadTeacherGroupDetail(selectedTeacherGroupId, "total", teacherGroupMemberPage);
        if (!canceled) setTeacherGroupMessage("");
      } catch {
        if (!canceled) setTeacherGroupMessage("Groepdetail laden is mislukt.");
      }
    }
    void run();
    return () => {
      canceled = true;
    };
  }, [selectedTeacherGroupId, teacherGroupMemberPage]);

  async function loadTeacherStudents() {
    setTeacherStudentsLoading(true);
    const params = new URLSearchParams();
    if (teacherStudentsQuery.trim()) params.set("q", teacherStudentsQuery.trim());
    if (teacherStudentsHasOpen !== "all") params.set("hasOpen", teacherStudentsHasOpen);
    params.set("sortBy", teacherStudentsSortBy);
    params.set("sortDir", teacherStudentsSortDir);
    params.set("page", String(teacherStudentsPage));
    params.set("pageSize", "20");
    const response = await fetch(`/api/v2/teacher/students?${params.toString()}`, { credentials: "include" });
    if (!response.ok) throw new Error(`teacher_students_failed_${response.status}`);
    const payload = await response.json() as {
      total?: number;
      items?: TeacherStudentListItem[];
    };
    const items = payload.items ?? [];
    setTeacherStudents(items);
    setTeacherStudentsTotal(Number(payload.total ?? items.length));
    setSelectedTeacherStudentId((current) => (items.some((row) => row.studentId === current) ? current : (items[0]?.studentId ?? "")));
    setTeacherStudentsError("");
    setTeacherStudentsLoading(false);
  }

  async function loadTeacherSettings() {
    const response = await fetch("/api/v2/teacher/settings", { credentials: "include" });
    if (!response.ok) throw new Error(`teacher_settings_failed_${response.status}`);
    const data = await response.json() as {
      maxActiveTickets?: number;
      acceptsNewTickets?: boolean;
      timezone?: string;
      availabilityWindows?: Array<{ dayOfWeek: number; startTime: string; endTime: string }>;
      availabilityExceptions?: Array<{ date: string; reason: string | null; blocked: boolean }>;
    };
    setTeacherMaxActiveTickets(String(data.maxActiveTickets ?? 25));
    setTeacherAcceptsNewTickets(Boolean(data.acceptsNewTickets ?? true));
    setTeacherTimezone(data.timezone ?? "Europe/Amsterdam");
    setTeacherAvailabilityWindows(data.availabilityWindows ?? []);
    setTeacherAvailabilityExceptions((data.availabilityExceptions ?? []).map((row) => ({
      date: row.date,
      reason: row.reason ?? "",
      blocked: row.blocked
    })));
  }

  async function saveTeacherSettings() {
    setTeacherSettingsLoading(true);
    setTeacherSettingsMessage("");
    try {
      const settingsResponse = await fetch("/api/v2/teacher/settings", {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          maxActiveTickets: Math.max(1, Number(teacherMaxActiveTickets || 25)),
          acceptsNewTickets: teacherAcceptsNewTickets,
          timezone: teacherTimezone.trim() || "Europe/Amsterdam"
        })
      });
      if (!settingsResponse.ok) throw new Error(`teacher_settings_save_failed_${settingsResponse.status}`);

      const availabilityResponse = await fetch("/api/v2/teacher/settings/availability", {
        method: "PUT",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          windows: teacherAvailabilityWindows,
          exceptions: teacherAvailabilityExceptions.map((row) => ({ date: row.date, reason: row.reason || undefined, blocked: row.blocked }))
        })
      });
      if (!availabilityResponse.ok) throw new Error(`teacher_availability_save_failed_${availabilityResponse.status}`);
      setTeacherSettingsMessage("Instellingen opgeslagen.");
    } catch {
      setTeacherSettingsMessage("Instellingen opslaan is mislukt.");
    } finally {
      setTeacherSettingsLoading(false);
    }
  }

  async function loadTeacherAssignments() {
    const [optionsResponse, batchesResponse, calendarResponse] = await Promise.all([
      fetch("/api/v2/teacher/ticket-generator/options", { credentials: "include" }),
      fetch("/api/v2/teacher/ticket-batches", { credentials: "include" }),
      fetch("/api/v2/teacher/calendar", { credentials: "include" })
    ]);
    if (optionsResponse.ok) {
      const options = await optionsResponse.json() as { groups?: TeacherAssignmentOptionGroup[]; ticketVersions?: TeacherAssignmentTicketVersion[] };
      const nextGroups = options.groups ?? [];
      const nextVersions = options.ticketVersions ?? [];
      setAssignmentGroups(nextGroups);
      setAssignmentVersions(nextVersions);
      setAssignmentClassId((current) => current || nextGroups[0]?.id || classes[0]?.classGroupId || "");
      setAssignmentVersionId((current) => current || nextVersions[0]?.id || activePublication.ticketVersionId);
    }
    if (batchesResponse.ok) {
      const payload = await batchesResponse.json() as { batches?: TeacherTicketBatchListItem[] };
      setTicketBatches(payload.batches ?? []);
    }
    if (calendarResponse.ok) {
      const payload = await calendarResponse.json() as TeacherCalendarSnapshot;
      setTeacherCalendar({
        publications: payload.publications ?? [],
        batches: payload.batches ?? []
      });
    }
  }

  async function createDirectAssignment() {
    setAssignmentBusy(true);
    setAssignmentMessage("");
    try {
      const response = await fetch("/api/v2/teacher/assignments", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          classGroupId: assignmentClassId,
          ticketVersionId: assignmentVersionId,
          dueAt: assignmentDueAt ? new Date(assignmentDueAt).toISOString() : undefined,
          attemptLimit: 3,
          feedbackMode: "teacher_released"
        })
      });
      if (!response.ok) throw new Error(`assignment_failed_${response.status}`);
      const payload = await response.json() as { publicationId?: string; assignedStudents?: number };
      setAssignmentMessage(`Directe uitgifte aangemaakt: publicatie ${payload.publicationId}, ${payload.assignedStudents ?? 0} studenten.`);
      await loadTeacherAssignments();
    } catch {
      setAssignmentMessage("Directe uitgifte is mislukt.");
    } finally {
      setAssignmentBusy(false);
    }
  }

  async function createTicketBatch() {
    setAssignmentBusy(true);
    setAssignmentMessage("");
    try {
      const response = await fetch("/api/v2/teacher/ticket-batches", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          classGroupId: assignmentClassId,
          ticketVersionIds: [assignmentVersionId],
          scheduledFor: assignmentScheduledFor ? new Date(assignmentScheduledFor).toISOString() : new Date().toISOString(),
          dueAt: assignmentDueAt ? new Date(assignmentDueAt).toISOString() : undefined,
          settings: { storyContext: assignmentStoryContext }
        })
      });
      if (!response.ok) throw new Error(`batch_failed_${response.status}`);
      const payload = await response.json() as { batchId?: string; status?: string };
      setAssignmentMessage(`Batch ${payload.batchId} staat op ${payload.status}.`);
      await loadTeacherAssignments();
    } catch {
      setAssignmentMessage("Batch aanmaken is mislukt.");
    } finally {
      setAssignmentBusy(false);
    }
  }

  async function rolloutTicketBatch(batchId: string) {
    const response = await fetch(`/api/v2/teacher/ticket-batches/${encodeURIComponent(batchId)}/rollout-now`, {
      method: "POST",
      credentials: "include"
    });
    setAssignmentMessage(response.ok ? `Batch ${batchId} is uitgerold.` : `Batch ${batchId} uitrollen is mislukt.`);
    await loadTeacherAssignments();
  }

  async function loadTeacherStudentDetail(studentId: string) {
    if (!studentId) return;
    const response = await fetch(`/api/v2/teacher/students/${encodeURIComponent(studentId)}`, { credentials: "include" });
    if (!response.ok) throw new Error(`teacher_student_detail_failed_${response.status}`);
    const payload = await response.json() as TeacherStudentDetail;
    setSelectedTeacherStudentDetail(payload);
  }

  useEffect(() => {
    let canceled = false;
    async function run() {
      try {
        await loadTeacherStudents();
      } catch {
        if (!canceled) {
          setTeacherStudentsError("Studentenoverzicht laden is mislukt.");
          setTeacherStudents([]);
          setTeacherStudentsTotal(0);
        }
      } finally {
        if (!canceled) setTeacherStudentsLoading(false);
      }
    }
    void run();
    return () => {
      canceled = true;
    };
  }, [teacherStudentsPage, teacherStudentsHasOpen, teacherStudentsSortBy, teacherStudentsSortDir, teacherStudentsQuery]);

  useEffect(() => {
    let canceled = false;
    void (async () => {
      try {
        await loadTeacherSettings();
      } catch {
        if (!canceled) setTeacherSettingsMessage("Instellingen laden is mislukt.");
      }
    })();
    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    void loadTeacherAssignments().catch(() => {
      setAssignmentMessage("Ticketuitgifte laden is mislukt.");
    });
  }, []);

  useEffect(() => {
    let canceled = false;
    async function run() {
      if (!selectedTeacherStudentId) {
        setSelectedTeacherStudentDetail(null);
        return;
      }
      try {
        await loadTeacherStudentDetail(selectedTeacherStudentId);
      } catch {
        if (!canceled) setSelectedTeacherStudentDetail(null);
      }
    }
    void run();
    return () => {
      canceled = true;
    };
  }, [selectedTeacherStudentId]);

  async function createTeacherGroup() {
    if (!newGroupName.trim() || teacherGroupBusy) return;
    setTeacherGroupBusy(true);
    setTeacherGroupMessage("");
    try {
      const response = await fetch("/api/v2/teacher/groups", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: newGroupName.trim() })
      });
      if (!response.ok) throw new Error(`teacher_group_create_failed_${response.status}`);
      const group = await response.json() as TeacherGroupListItem;
      setNewGroupName("");
      setSelectedTeacherGroupId(group.id);
      await loadTeacherGroups();
      await loadTeacherGroupDetail(group.id);
      setTeacherGroupMessage(`Groep ${group.name} is aangemaakt met join-code ${group.joinCode}.`);
    } catch {
      setTeacherGroupMessage("Groep aanmaken is mislukt.");
    } finally {
      setTeacherGroupBusy(false);
    }
  }

  async function renameTeacherGroup() {
    if (!selectedTeacherGroupId || !groupRenameDraft.trim() || teacherGroupBusy) return;
    setTeacherGroupBusy(true);
    setTeacherGroupMessage("");
    try {
      const response = await fetch(`/api/v2/teacher/groups/${encodeURIComponent(selectedTeacherGroupId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ name: groupRenameDraft.trim() })
      });
      if (!response.ok) throw new Error(`teacher_group_rename_failed_${response.status}`);
      await loadTeacherGroups();
      await loadTeacherGroupDetail(selectedTeacherGroupId);
      setTeacherGroupMessage("Groepsnaam opgeslagen.");
    } catch {
      setTeacherGroupMessage("Groep hernoemen is mislukt.");
    } finally {
      setTeacherGroupBusy(false);
    }
  }

  async function archiveTeacherGroup() {
    if (!selectedTeacherGroupId || teacherGroupBusy) return;
    setTeacherGroupBusy(true);
    setTeacherGroupMessage("");
    try {
      const response = await fetch(`/api/v2/teacher/groups/${encodeURIComponent(selectedTeacherGroupId)}/archive`, {
        method: "POST",
        credentials: "include"
      });
      if (!response.ok) throw new Error(`teacher_group_archive_failed_${response.status}`);
      await loadTeacherGroups();
      await loadTeacherGroupDetail(selectedTeacherGroupId);
      setTeacherGroupMessage("Groep is gearchiveerd.");
    } catch {
      setTeacherGroupMessage("Groep archiveren is mislukt.");
    } finally {
      setTeacherGroupBusy(false);
    }
  }

  async function addTeacherToGroup() {
    if (!selectedTeacherGroupId || !teacherToAdd || teacherGroupBusy) return;
    setTeacherGroupBusy(true);
    setTeacherGroupMessage("");
    try {
      const response = await fetch(`/api/v2/teacher/groups/${encodeURIComponent(selectedTeacherGroupId)}/teachers`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ teacherUserId: teacherToAdd })
      });
      if (!response.ok) throw new Error(`teacher_group_add_teacher_failed_${response.status}`);
      await loadTeacherGroupDetail(selectedTeacherGroupId);
      setTeacherGroupMessage("Co-docent toegevoegd.");
    } catch {
      setTeacherGroupMessage("Co-docent toevoegen is mislukt.");
    } finally {
      setTeacherGroupBusy(false);
    }
  }

  async function removeTeacherFromGroup(teacherUserId: string) {
    if (!selectedTeacherGroupId || teacherGroupBusy) return;
    setTeacherGroupBusy(true);
    setTeacherGroupMessage("");
    try {
      const response = await fetch(`/api/v2/teacher/groups/${encodeURIComponent(selectedTeacherGroupId)}/teachers/${encodeURIComponent(teacherUserId)}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (!response.ok) throw new Error(`teacher_group_remove_teacher_failed_${response.status}`);
      await loadTeacherGroupDetail(selectedTeacherGroupId);
      setTeacherGroupMessage("Co-docent verwijderd.");
    } catch {
      setTeacherGroupMessage("Co-docent verwijderen is mislukt.");
    } finally {
      setTeacherGroupBusy(false);
    }
  }

  async function removeStudentFromGroup(studentId: string) {
    if (!selectedTeacherGroupId || teacherGroupBusy) return;
    setTeacherGroupBusy(true);
    setTeacherGroupMessage("");
    try {
      const response = await fetch(`/api/v2/teacher/groups/${encodeURIComponent(selectedTeacherGroupId)}/members/${encodeURIComponent(studentId)}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (!response.ok) throw new Error(`teacher_group_remove_student_failed_${response.status}`);
      await loadTeacherGroups();
      await loadTeacherGroupDetail(selectedTeacherGroupId);
      setTeacherGroupMessage("Student uit groep verwijderd.");
    } catch {
      setTeacherGroupMessage("Student verwijderen is mislukt.");
    } finally {
      setTeacherGroupBusy(false);
    }
  }

  async function resetStudentPassword(studentId: string) {
    if (!selectedTeacherGroupId || teacherGroupBusy) return;
    setTeacherGroupBusy(true);
    setTeacherGroupMessage("");
    try {
      const response = await fetch(`/api/v2/teacher/groups/${encodeURIComponent(selectedTeacherGroupId)}/students/${encodeURIComponent(studentId)}/reset-password`, {
        method: "POST",
        credentials: "include"
      });
      if (!response.ok) throw new Error(`teacher_group_reset_password_failed_${response.status}`);
      const data = await response.json() as { temporaryPassword?: string };
      setTeacherGroupMessage(`Tijdelijk wachtwoord: ${data.temporaryPassword ?? "aangemaakt"}. Deel dit buiten git/docs om.`);
    } catch {
      setTeacherGroupMessage("Wachtwoord resetten is mislukt.");
    } finally {
      setTeacherGroupBusy(false);
    }
  }

  async function handleEvaluateGroupAttempt(attempt: TeacherGroupAttemptDetail) {
    setEvaluatingAttemptId(attempt.attemptId);
    setEvaluationError("");

    try {
      const response = await fetch(`/api/v2/assessment/attempts/${attempt.attemptId}/evaluate`, {
        method: "POST",
        credentials: "include"
      });

      if (!response.ok) {
        throw new Error(`evaluate failed: ${response.status}`);
      }

      const summary = await response.json() as EvaluationSummary;
      setEvaluationNotice({
        attemptId: attempt.attemptId,
        ticketNumber: attempt.ticketNumber,
        ticketTitle: attempt.ticketTitle,
        studentName: attempt.studentName,
        summary
      });
      if (selectedTeacherGroupId) {
        await loadTeacherGroupDetail(selectedTeacherGroupId);
      }
      await onRefresh();
    } catch {
      setEvaluationError("Evalueren is niet gelukt. Controleer de attempt en probeer opnieuw.");
    } finally {
      setEvaluatingAttemptId(null);
    }
  }

  async function openScenarioReview(attemptId: string) {
    setScenarioReviewLoading(true);
    setScenarioReviewError("");
    setScenarioFeedbackMessage("");
    setScenarioFeedbackText("");
    setScenarioFeedbackScore("");
    try {
      const response = await fetch(`/api/v2/teacher/scenario-instances/${encodeURIComponent(attemptId)}`, {
        credentials: "include"
      });
      if (!response.ok) throw new Error(`teacher_scenario_instance_failed_${response.status}`);
      const payload = await response.json() as TeacherScenarioInstanceDetail;
      setScenarioReview(payload);
    } catch {
      setScenarioReview(null);
      setScenarioReviewError("Scenario-review laden is mislukt.");
    } finally {
      setScenarioReviewLoading(false);
    }
  }

  async function submitScenarioFeedback() {
    if (!scenarioReview || scenarioFeedbackSaving || !scenarioFeedbackText.trim()) return;
    setScenarioFeedbackSaving(true);
    setScenarioFeedbackMessage("");
    try {
      const response = await fetch(`/api/v2/teacher/scenario-instances/${encodeURIComponent(scenarioReview.attempt.attemptId)}/feedback`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          bodyText: scenarioFeedbackText.trim(),
          score: scenarioFeedbackScore.trim() ? Number(scenarioFeedbackScore) : undefined
        })
      });
      if (!response.ok) throw new Error(`teacher_scenario_feedback_failed_${response.status}`);
      await openScenarioReview(scenarioReview.attempt.attemptId);
      setScenarioFeedbackMessage("Feedback opgeslagen.");
    } catch {
      setScenarioFeedbackMessage("Feedback opslaan is mislukt.");
    } finally {
      setScenarioFeedbackSaving(false);
    }
  }

  function resetBuilder() {
    setBuilderStep(1);
    setBuilderError("");
    setNewTemplateId(null);
    setNewVersionId(null);
    setTemplateDraft({
      title: "",
      description: "",
      topic: "networking",
      complexity: "intermediate",
      category: "game",
      environment: "hybrid",
      skills: "",
      learningObjectives: ""
    });
    setTargetsDraft([{
      id: "target-1",
      title: "",
      safeTitle: "",
      description: "",
      visibility: "visible",
      evaluatorKey: "network.client_dns",
      weight: "1",
      scoring: "automatic",
      expected: "{}",
      teacherNotes: ""
    }]);
    setPublicationDraft((current) => ({
      ...current,
      classGroupId: classes[0]?.classGroupId ?? ""
    }));
  }

  function updateTargetDraft(index: number, patch: Partial<(typeof targetsDraft)[number]>) {
    setTargetsDraft((current) => current.map((target, targetIndex) => (
      targetIndex === index ? { ...target, ...patch } : target
    )));
  }

  function addTargetDraft() {
    setTargetsDraft((current) => [...current, {
      id: `target-${current.length + 1}`,
      title: "",
      safeTitle: "",
      description: "",
      visibility: "visible",
      evaluatorKey: "network.client_dns",
      weight: "1",
      scoring: "automatic",
      expected: "{}",
      teacherNotes: ""
    }]);
  }

  function removeTargetDraft(index: number) {
    setTargetsDraft((current) => current.length <= 1 ? current : current.filter((_, targetIndex) => targetIndex !== index));
  }

  async function createTemplateStep() {
    if (builderBusy) return;
    setBuilderError("");
    setBuilderBusy(true);
    try {
      const response = await fetch("/api/v2/content/ticket-templates", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          title: templateDraft.title,
          description: templateDraft.description,
          topic: templateDraft.topic,
          complexity: templateDraft.complexity,
          category: templateDraft.category,
          environment: templateDraft.environment,
          skills: templateDraft.skills.split(",").map((part) => part.trim()).filter(Boolean),
          learningObjectives: templateDraft.learningObjectives.split("\n").map((part) => part.trim()).filter(Boolean)
        })
      });
      if (!response.ok) {
        setBuilderError("Template aanmaken is mislukt.");
        return;
      }
      const template = await response.json() as { id: string };
      setNewTemplateId(template.id);
      setBuilderStep(2);
    } catch {
      setBuilderError("Template aanmaken is tijdelijk niet beschikbaar.");
    } finally {
      setBuilderBusy(false);
    }
  }

  async function publishVersionStep() {
    if (!newTemplateId) return;
    if (builderBusy) return;
    setBuilderError("");
    setBuilderBusy(true);
    const targets = targetsDraft.map((target) => ({
      id: target.id,
      title: target.title,
      safeTitle: target.safeTitle || undefined,
      description: target.description || undefined,
      visibility: target.visibility,
      weight: Number(target.weight || "1"),
      evaluatorKey: target.evaluatorKey,
      scoring: target.scoring,
      expected: (() => {
        try {
          return JSON.parse(target.expected || "{}");
        } catch {
          return {};
        }
      })(),
      teacherNotes: target.teacherNotes || undefined
    }));
    const hasInvalidJson = targetsDraft.some((target) => {
      try {
        JSON.parse(target.expected || "{}");
        return false;
      } catch {
        return true;
      }
    });
    if (hasInvalidJson) {
      setBuilderError("Eén of meer target expected-velden bevatten geen geldige JSON.");
      setBuilderBusy(false);
      return;
    }
    try {
      const response = await fetch(`/api/v2/content/ticket-templates/${newTemplateId}/publish`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ persist: true, targets })
      });
      if (!response.ok) {
        setBuilderError("Publiceren van templateversie is mislukt.");
        return;
      }
      const version = await response.json() as { id: string };
      setNewVersionId(version.id);
      setBuilderStep(3);
    } catch {
      setBuilderError("Publiceren van templateversie is tijdelijk niet beschikbaar.");
    } finally {
      setBuilderBusy(false);
    }
  }

  async function publishClassStep() {
    if (!newVersionId || !publicationDraft.classGroupId) return;
    if (builderBusy) return;
    setBuilderError("");
    setBuilderBusy(true);
    try {
      const response = await fetch("/api/v2/game/publications", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ticketVersionId: newVersionId,
          classGroupId: publicationDraft.classGroupId,
          availableFrom: new Date(publicationDraft.availableFrom).toISOString(),
          availableUntil: publicationDraft.availableUntil ? new Date(publicationDraft.availableUntil).toISOString() : null,
          attemptLimit: Number(publicationDraft.attemptLimit || "1"),
          feedbackMode: publicationDraft.feedbackMode
        })
      });
      if (!response.ok) {
        setBuilderError("Publiceren naar klas is mislukt.");
        return;
      }
      await onRefresh();
      setBuilderOpen(false);
      resetBuilder();
    } catch {
      setBuilderError("Publiceren naar klas is tijdelijk niet beschikbaar.");
    } finally {
      setBuilderBusy(false);
    }
  }

  return (
    <>
      <header className="fd-app-page-header" id="teacher-overview">
        <div className="fd-app-page-header-left">
          <span className="fd-app-kicker">Docent Dashboard</span>
          <h1>Docentomgeving</h1>
          <p className="fd-app-page-desc">Volg studenten, beheer game tickets en bewaak de klasvoortgang.</p>
        </div>
        <div className="fd-segment" aria-label="Dashboard periode">
          {teacherPeriodOptions.map((option) => (
            <button
              className={`fd-segment-btn${teacherDashboardPeriod === option.value ? " fd-segment-btn--active" : ""}`}
              data-dashboard-period={option.value}
              key={option.value}
              onClick={() => setTeacherDashboardPeriod(option.value)}
              type="button"
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>

      <section className="fd-kpi-grid">
        <div className="fd-kpi-card">
          <div className="fd-kpi-card-bg-icon"><GraduationCap aria-hidden="true" size={46} /></div>
          <p className="fd-kpi-label">Eigen studenten</p>
          <div className="fd-kpi-value-row">
            <h3 className="fd-kpi-value">{uniqueStudents}</h3>
            <span className="fd-kpi-delta fd-kpi-delta--up">{periodDeltaLabel}</span>
          </div>
          <div className="fd-kpi-avatar-row" aria-label={`${uniqueStudents} studenten in beeld`}>
            {teacherAvatarStudents.map((student) => (
              <span className="fd-kpi-avatar-chip" key={student} title={student}>{initials(student)}</span>
            ))}
            {teacherAvatarOverflow > 0 ? <span className="fd-kpi-avatar-chip fd-kpi-avatar-chip--more">+{teacherAvatarOverflow}</span> : null}
          </div>
        </div>
        <div className="fd-kpi-card">
          <div className="fd-kpi-card-bg-icon"><TicketCheck aria-hidden="true" size={46} /></div>
          <p className="fd-kpi-label">Open tickets</p>
          <div className="fd-kpi-value-row">
            <h3 className="fd-kpi-value">{liveBoardRows.length}</h3>
            <span className="fd-kpi-delta fd-kpi-delta--warn">{reviewPercent}% review</span>
          </div>
          <div className="fd-kpi-progress" aria-label={`${classProgressPercent}% klasvoortgang`}>
            <span className="fd-kpi-progress-fill" style={{ width: `${classProgressPercent}%` }} />
          </div>
        </div>
        <div className="fd-kpi-card fd-kpi-card--primary">
          <div className="fd-kpi-card-bg-icon"><Activity aria-hidden="true" size={46} /></div>
          <p className="fd-kpi-label">Nu actief</p>
          <div className="fd-kpi-value-row">
            <h3 className="fd-kpi-value">{activeAttemptCount}</h3>
            <div className="fd-live-indicator" aria-label={`${activeAttemptCount} actieve attempts`}>
              <span className="fd-live-dot" />
              <span className="fd-live-text">{activeAttemptCount > 0 ? "Live lab" : "Stand-by"}</span>
            </div>
          </div>
          <div className="fd-kpi-progress fd-kpi-progress--primary" aria-label={`${activePercent}% nu actief`}>
            <span className="fd-kpi-progress-fill fd-kpi-progress-fill--live" style={{ width: `${activePercent}%` }} />
          </div>
        </div>
        <div className="fd-kpi-card">
          <div className="fd-kpi-card-head-row">
            <p className="fd-kpi-label">Nieuwe tickets</p>
            <div className="fd-toggle fd-toggle--on"><div className="fd-toggle-thumb" /></div>
          </div>
          <div className="fd-kpi-value-row">
            <h3 className="fd-kpi-value">{versionPreview.targetsSnapshot.length}</h3>
            <span className="fd-kpi-delta">Targets</span>
          </div>
          <div className="fd-kpi-progress" aria-label={`${ticketCoveragePercent}% targetdekking`}>
            <span className="fd-kpi-progress-fill fd-kpi-progress-fill--orange" style={{ width: `${ticketCoveragePercent}%` }} />
          </div>
        </div>
      </section>

      <div className="fd-app-grid-2-1 fd-v2-teacher-grid">
        <div id="teacher-students">
          <div className="fd-section-head">
            <h2 className="fd-section-title">
              <span className="material-symbols-outlined">person</span>
              Mijn studenten
            </h2>
            <a className="fd-link-bold" href="#teacher-groups">Bekijk groepen</a>
          </div>
          <div className="fd-v2-teacher-student-kpis">
            <span className="fd-status-badge fd-status-badge--active">Totaal {teacherStudentsTotal}</span>
            <span className="fd-status-badge">{teacherStudents.reduce((sum, row) => sum + row.openCount, 0)} open</span>
            <span className="fd-status-badge">{teacherStudents.reduce((sum, row) => sum + row.completedCount, 0)} afgerond</span>
            <span className="fd-status-badge">{teacherStudents.reduce((sum, row) => sum + row.reviewCount, 0)} te beoordelen</span>
          </div>
          <div className="fd-v2-group-filters">
            <label>
              Zoek
              <input
                value={teacherStudentsQuery}
                onChange={(event) => {
                  setTeacherStudentsPage(1);
                  setTeacherStudentsQuery(event.target.value);
                }}
                placeholder="Naam of e-mail"
              />
            </label>
            <label>
              Open tickets
              <select value={teacherStudentsHasOpen} onChange={(event) => {
                setTeacherStudentsPage(1);
                setTeacherStudentsHasOpen(event.target.value as "all" | "true" | "false");
              }}>
                <option value="all">Alles</option>
                <option value="true">Alleen open</option>
                <option value="false">Geen open</option>
              </select>
            </label>
            <label>
              Sorteer op
              <select value={teacherStudentsSortBy} onChange={(event) => setTeacherStudentsSortBy(event.target.value as "name" | "open" | "completed" | "review")}>
                <option value="name">Naam</option>
                <option value="open">Open tickets</option>
                <option value="completed">Afgerond</option>
                <option value="review">Te beoordelen</option>
              </select>
            </label>
            <label>
              Richting
              <select value={teacherStudentsSortDir} onChange={(event) => setTeacherStudentsSortDir(event.target.value as "asc" | "desc")}>
                <option value="asc">Oplopend</option>
                <option value="desc">Aflopend</option>
              </select>
            </label>
          </div>
          <div className="fd-app-table-wrap">
            <table className="fd-app-table">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>Klas</th>
                  <th className="fd-col-center">Open</th>
                  <th className="fd-col-center">Afgerond</th>
                  <th className="fd-col-center">Review</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {teacherStudents.map((row) => {
                  return (
                    <tr key={row.studentId}>
                      <td>
                        <div className="fd-table-user">
                          <div className="fd-table-avatar">{initials(row.name)}</div>
                          <div>
                            <p className="fd-table-name">{row.name}</p>
                            <p className="fd-table-sub">{row.email}</p>
                          </div>
                        </div>
                      </td>
                      <td>{row.className ?? "-"}</td>
                      <td className="fd-col-center">{row.openCount}</td>
                      <td className="fd-col-center">{row.completedCount}</td>
                      <td className="fd-col-center">{row.reviewCount}</td>
                      <td className="fd-col-right">
                        <button
                          className="fd-table-action fd-table-action--secondary"
                          type="button"
                          onClick={() => setSelectedTeacherStudentId(row.studentId)}
                        >
                          Detail
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="fd-v2-row-between">
            <button className="fd-app-btn fd-app-btn--outline" type="button" disabled={teacherStudentsPage <= 1} onClick={() => setTeacherStudentsPage((page) => Math.max(1, page - 1))}>Vorige</button>
            <span className="fd-table-sub">Pagina {teacherStudentsPage}</span>
            <button className="fd-app-btn fd-app-btn--outline" type="button" disabled={teacherStudents.length < 20} onClick={() => setTeacherStudentsPage((page) => page + 1)}>Volgende</button>
          </div>
          {teacherStudentsLoading ? <p className="fd-table-sub">Studenten laden...</p> : null}
          {teacherStudentsError ? <p className="fd-table-sub">{teacherStudentsError}</p> : null}
          {selectedTeacherStudentDetail ? (
            <section className="fd-v2-student-progress-card">
              <div className="fd-v2-row-between">
                <div>
                  <span className="fd-v2-create-eyebrow">Studentdetail</span>
                  <h4>{selectedTeacherStudentDetail.student.name}</h4>
                  <p>{selectedTeacherStudentDetail.student.email}</p>
                </div>
                <span className="fd-status-badge">{selectedTeacherStudentDetail.attempts.length} attempts</span>
              </div>
              <div className="fd-v2-attempt-list">
                {selectedTeacherStudentDetail.attempts.slice(0, 8).map((attempt) => (
                  <article className="fd-v2-attempt-card" key={attempt.attemptId}>
                    <div>
                      <strong>Ticket {attempt.ticketNumber} - {attempt.ticketTitle}</strong>
                      <p>{attempt.submittedAt ? new Date(attempt.submittedAt).toLocaleString("nl-NL") : "Nog niet ingeleverd"}</p>
                    </div>
                    <span className="fd-status-badge">{attempt.status}</span>
                    <button
                      className="fd-table-action"
                      type="button"
                      onClick={() => void openScenarioReview(attempt.attemptId)}
                    >
                      Review scenario
                    </button>
                  </article>
                ))}
              </div>
            </section>
          ) : null}
          {evaluationNotice ? (
            <div className="fd-announce-card fd-teacher-eval-card">
              <div className="fd-v2-row-between">
                <h4 className="fd-announce-title">Evaluatie opgeslagen</h4>
                <span className="fd-status-badge fd-status-badge--active">Attempt {evaluationNotice.attemptId}</span>
              </div>
              <p className="fd-announce-text">
                Ticket {evaluationNotice.ticketNumber} voor {evaluationNotice.studentName} is beoordeeld.
                {` ${evaluationNotice.summary.passedTargets}/${evaluationNotice.summary.totalTargets} targets staan nu groen.`}
              </p>
              <div className="fd-ticket-feedback-pills">
                <span className="target-pill is-passed">{evaluationNotice.summary.passedTargets} passed</span>
                <span className="target-pill is-open">{evaluationNotice.summary.totalTargets - evaluationNotice.summary.passedTargets} resterend</span>
              </div>
            </div>
          ) : null}
          {evaluationError ? (
            <div className="fd-announce-card fd-teacher-eval-card fd-teacher-eval-card--error">
              <h4 className="fd-announce-title">Evaluatie mislukt</h4>
              <p className="fd-announce-text">{evaluationError}</p>
            </div>
          ) : null}
        </div>

        <div>
          <div className="fd-section-head">
            <h2 className="fd-section-title">Groepenbeheer</h2>
          </div>
          <div className="fd-bento-stat fd-bento-stat--white fd-v2-side-card">
            <a className="fd-group-mini" href="#publication">
              <div className="fd-v2-row-between">
                <span className="fd-group-mini-name">Actieve publicatie</span>
                <span className="fd-group-mini-count">{activePublication.status}</span>
              </div>
              <div className="fd-app-progress-wrap">
                <div className="fd-app-progress" style={{ "--fd-progress-pct": "68%" } as CSSProperties} />
              </div>
              <p className="fd-table-sub">Privacy: {liveBoardSession.privacyMode}</p>
            </a>
            <button
              className="fd-bento-open-btn"
              type="button"
              onClick={() => {
                resetBuilder();
                setBuilderOpen(true);
              }}
            >
              <Gamepad2 aria-hidden="true" size={18} />
              Nieuw game-ticket
            </button>
          </div>

          <div className="fd-announce-card fd-v2-visibility-card">
            <h4 className="fd-announce-title">Target visibility</h4>
            <div className="visibility-list">
              {versionPreview.targetsSnapshot.slice(0, 4).map((target) => (
                <article className="visibility-item" key={target.id}>
                  <span className="visibility-icon">
                    {target.visibility === "visible" ? <Eye aria-hidden="true" size={16} /> : <LockKeyhole aria-hidden="true" size={16} />}
                  </span>
                  <div>
                    <strong>{target.title}</strong>
                    <small>{target.visibility}</small>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>

      <section id="teacher-assignments" className="fd-v2-assignment-panel">
        <div className="fd-section-head">
          <h2 className="fd-section-title">
            <ListChecks aria-hidden="true" size={20} />
            Ticketuitgifte
          </h2>
          <span className="fd-status-badge">{ticketBatches.length} batches</span>
        </div>
        <div className="fd-v2-assignment-grid">
          <div className="fd-bento-stat fd-bento-stat--white">
            <h3>Directe uitgifte</h3>
            <div className="fd-v2-group-filters fd-v2-assignment-form">
              <label>
                Klas
                <select value={assignmentClassId} onChange={(event) => setAssignmentClassId(event.target.value)}>
                  {(assignmentGroups.length > 0 ? assignmentGroups : classes.map((group) => ({
                    id: group.classGroupId,
                    name: group.className,
                    code: group.classCode,
                    studentCount: group.studentCount
                  }))).map((group) => (
                    <option value={group.id} key={group.id}>{group.name} ({group.studentCount})</option>
                  ))}
                </select>
              </label>
              <label>
                Ticket
                <select value={assignmentVersionId} onChange={(event) => setAssignmentVersionId(event.target.value)}>
                  {(assignmentVersions.length > 0 ? assignmentVersions : [{
                    id: activePublication.ticketVersionId,
                    templateId: template.id,
                    versionNumber: 1,
                    title: template.title,
                    topic: template.topic,
                    complexity: template.complexity
                  }]).map((version) => (
                    <option value={version.id} key={version.id}>{version.title} v{version.versionNumber}</option>
                  ))}
                </select>
              </label>
              <label>
                Deadline
                <input type="datetime-local" value={assignmentDueAt} onChange={(event) => setAssignmentDueAt(event.target.value)} />
              </label>
            </div>
            <button className="fd-bento-open-btn" type="button" disabled={assignmentBusy || !assignmentClassId || !assignmentVersionId} onClick={() => void createDirectAssignment()}>
              <Send aria-hidden="true" size={18} />
              Geef direct uit
            </button>
          </div>

          <div className="fd-bento-stat fd-bento-stat--white">
            <h3>Batch plannen</h3>
            <div className="fd-v2-group-filters fd-v2-assignment-form">
              <label>
                Planning
                <input type="datetime-local" value={assignmentScheduledFor} onChange={(event) => setAssignmentScheduledFor(event.target.value)} />
              </label>
              <label>
                Story-context
                <textarea value={assignmentStoryContext} onChange={(event) => setAssignmentStoryContext(event.target.value)} rows={3} />
              </label>
            </div>
            <button className="fd-app-btn fd-app-btn--primary" type="button" disabled={assignmentBusy || !assignmentClassId || !assignmentVersionId} onClick={() => void createTicketBatch()}>
              <CalendarDays aria-hidden="true" size={18} />
              Plan batch
            </button>
          </div>
        </div>
        {assignmentMessage ? <p className="fd-table-sub">{assignmentMessage}</p> : null}
        <div className="fd-v2-assignment-grid">
          <div className="fd-app-table-wrap">
            <table className="fd-app-table">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Klas</th>
                  <th>Status</th>
                  <th>Tickets</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {ticketBatches.slice(0, 8).map((batch) => (
                  <tr key={batch.id}>
                    <td>#{batch.id}</td>
                    <td>{batch.className ?? batch.classGroupId ?? "-"}</td>
                    <td><span className="fd-status-badge">{batch.status}</span></td>
                    <td>{batch.publicationCount}</td>
                    <td className="fd-col-right">
                      <button className="fd-table-action" type="button" onClick={() => void rolloutTicketBatch(batch.id)}>Nu uitrollen</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="fd-bento-stat fd-bento-stat--white">
            <h3>Gedeelde kalender</h3>
            <div className="fd-v2-calendar-list">
              {teacherCalendar.publications.slice(0, 5).map((publication) => (
                <div className="fd-v2-calendar-row" key={`pub-${publication.id}`}>
                  <span>Publicatie #{publication.id}</span>
                  <strong>{new Date(publication.availableFrom).toLocaleDateString("nl-NL")}</strong>
                </div>
              ))}
              {teacherCalendar.batches.slice(0, 5).map((batch) => (
                <div className="fd-v2-calendar-row" key={`batch-${batch.id}`}>
                  <span>Batch #{batch.id}</span>
                  <strong>{batch.scheduledFor ? new Date(batch.scheduledFor).toLocaleDateString("nl-NL") : "Direct"}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <WebsiteModal
        open={scenarioReviewLoading || Boolean(scenarioReview) || Boolean(scenarioReviewError)}
        title="Scenario instance review"
        onClose={() => {
          setScenarioReview(null);
          setScenarioReviewError("");
          setScenarioFeedbackText("");
          setScenarioFeedbackScore("");
          setScenarioFeedbackMessage("");
        }}
      >
        {scenarioReviewLoading ? <p>Scenario laden...</p> : null}
        {scenarioReviewError ? <p>{scenarioReviewError}</p> : null}
        {scenarioReview ? (
          <div className="fd-stack-md">
            <p><strong>{scenarioReview.attempt.ticket.number}</strong> - {scenarioReview.attempt.ticket.title}</p>
            <p>{scenarioReview.attempt.student.name} ({scenarioReview.attempt.student.email})</p>
            <p>Status: {scenarioReview.attempt.status}</p>
            <label className="fd-builder-field">
              Feedback
              <textarea value={scenarioFeedbackText} onChange={(event) => setScenarioFeedbackText(event.target.value)} rows={4} />
            </label>
            <label className="fd-builder-field">
              Score (optioneel)
              <input
                type="number"
                min={0}
                max={100}
                value={scenarioFeedbackScore}
                onChange={(event) => setScenarioFeedbackScore(event.target.value)}
              />
            </label>
            <button className="fd-app-btn" type="button" disabled={scenarioFeedbackSaving || !scenarioFeedbackText.trim()} onClick={() => void submitScenarioFeedback()}>
              {scenarioFeedbackSaving ? "Opslaan..." : "Feedback opslaan"}
            </button>
            {scenarioFeedbackMessage ? <p>{scenarioFeedbackMessage}</p> : null}
            <div className="fd-stack-sm">
              <h4>Feedbackhistorie</h4>
              {scenarioReview.feedbackHistory.length === 0 ? <p>Nog geen feedback.</p> : scenarioReview.feedbackHistory.map((item) => (
                <article key={item.id} className="fd-v2-attempt-card">
                  <p><strong>{item.teacher.name}</strong> ({new Date(item.createdAt).toLocaleString("nl-NL")})</p>
                  <p>{item.bodyText}</p>
                  <p>Score: {item.score ?? "-"}</p>
                </article>
              ))}
            </div>
          </div>
        ) : null}
      </WebsiteModal>

      <section className="fd-bento-stat fd-bento-stat--white fd-v2-content-editor" id="teacher-groups">
        <div className="fd-section-head">
          <h2 className="fd-section-title">
            <GraduationCap aria-hidden="true" size={19} />
            Groepenbeheer
          </h2>
          <span className="fd-status-badge fd-status-badge--active">{teacherGroups.length} groepen</span>
        </div>

        <div className="fd-v2-group-create-card">
          <div>
            <span className="fd-v2-create-eyebrow">Nieuwe groep</span>
            <h3>Nieuwe groep aanmaken</h3>
            <p>Maak een klasgroep aan en deel de join-code met studenten. Dit volgt de v1-groepenflow, maar schrijft naar de v2-database.</p>
          </div>
          <label className="fd-v2-group-create-field">
            Groepsnaam
            <input
              value={newGroupName}
              onChange={(event) => setNewGroupName(event.target.value)}
              placeholder="Bijvoorbeeld: ICT Basis 2026-A"
            />
          </label>
          <button className="fd-app-btn fd-app-btn--primary" type="button" onClick={() => void createTeacherGroup()} disabled={!newGroupName.trim() || teacherGroupBusy}>
            <UserPlus aria-hidden="true" size={17} />
            Groep aanmaken
          </button>
        </div>

        <div className="fd-v2-admin-user-toolbar">
          <label>
            Zoeken
            <input
              value={teacherGroupSearch}
              onChange={(event) => setTeacherGroupSearch(event.target.value)}
              placeholder="Zoek op groepsnaam of join-code"
            />
          </label>
          <label>
            Status
            <select value={teacherGroupStatus} onChange={(event) => setTeacherGroupStatus(event.target.value as typeof teacherGroupStatus)}>
              <option value="active">Actief</option>
              <option value="archived">Gearchiveerd</option>
              <option value="all">Alle groepen</option>
            </select>
          </label>
          <label>
            Sortering
            <select value={teacherGroupSort} onChange={(event) => setTeacherGroupSort(event.target.value)}>
              <option value="progress">Voortgang</option>
              <option value="students">Studenten</option>
              <option value="name">Naam</option>
              <option value="createdAt">Aangemaakt</option>
            </select>
          </label>
        </div>

        {teacherGroupMessage ? <p className="fd-v2-inline-message">{teacherGroupMessage}</p> : null}

        <div className="fd-v2-teacher-groups-grid">
          <div className="fd-app-table-wrap">
            <table className="fd-app-table">
              <thead>
                <tr>
                  <th>Groep</th>
                  <th>Join-code</th>
                  <th className="fd-col-center">Studenten</th>
                  <th className="fd-col-center">Voortgang</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {teacherGroups.map((group) => (
                  <tr
                    key={group.id}
                    onClick={() => setSelectedTeacherGroupId(group.id)}
                    style={{ cursor: "pointer", background: selectedTeacherGroupId === group.id ? "rgba(24, 119, 242, 0.08)" : undefined }}
                  >
                    <td>
                      <p className="fd-table-name">{group.name}</p>
                      <p className="fd-table-sub">{group.publicationCount} publicaties, {group.attemptCount} attempts</p>
                    </td>
                    <td><span className="fd-status-badge">{group.joinCode}</span></td>
                    <td className="fd-col-center"><span className="fd-table-score">{group.studentCount}</span></td>
                    <td className="fd-col-center">
                      <span className="fd-table-score">{group.progressPercent}%</span>
                      <div className="fd-app-progress-wrap"><div className="fd-app-progress" style={{ "--fd-progress-pct": `${group.progressPercent}%` } as CSSProperties} /></div>
                    </td>
                    <td><span className="fd-status-badge fd-status-badge--active">{group.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {teacherGroups.length === 0 ? <p className="fd-table-sub">Geen groepen gevonden voor deze filters.</p> : null}
          </div>

          <div className="fd-v2-group-detail">
            {selectedTeacherGroup ? (
              <>
                <div className="fd-section-head">
                  <div>
                    <h3 className="fd-section-title">{selectedTeacherGroup.group.name}</h3>
                    <p className="fd-table-sub">Join-code {selectedTeacherGroup.group.joinCode} - {selectedTeacherGroup.group.studentCount} studenten</p>
                  </div>
                  <div className="fd-v2-row-actions">
                    <a className="fd-app-btn fd-app-btn--outline" href={`/api/v2/teacher/groups/${encodeURIComponent(selectedTeacherGroup.group.id)}/export.csv`}>
                      <Download aria-hidden="true" size={16} />
                      CSV
                    </a>
                    <button className="fd-app-btn fd-app-btn--outline" type="button" onClick={() => void archiveTeacherGroup()} disabled={teacherGroupBusy}>
                      <Archive aria-hidden="true" size={16} />
                      Archiveer
                    </button>
                  </div>
                </div>

                <div className="fd-v2-inline-form">
                  <label>
                    Groepsnaam
                    <input value={groupRenameDraft} onChange={(event) => setGroupRenameDraft(event.target.value)} />
                  </label>
                  <button className="fd-app-btn" type="button" onClick={() => void renameTeacherGroup()} disabled={teacherGroupBusy || !groupRenameDraft.trim() || groupRenameDraft === selectedTeacherGroup.group.name}>
                    Opslaan
                  </button>
                </div>

                <div className="fd-v2-group-columns">
                  <section>
                    <div className="fd-section-head">
                      <h4>Top 20</h4>
                      <select value={selectedTeacherGroup.period} onChange={(event) => void loadTeacherGroupDetail(selectedTeacherGroup.group.id, event.target.value)}>
                        <option value="day">Dag</option>
                        <option value="week">Week</option>
                        <option value="month">Maand</option>
                        <option value="year">Jaar</option>
                        <option value="total">Totaal</option>
                      </select>
                    </div>
                    <ol className="fd-v2-ranking-list">
                      {selectedTeacherGroup.ranking.map((row, index) => (
                        <li key={row.studentId}>
                          <span>{index + 1}</span>
                          <strong>{row.studentName}</strong>
                          <small>{row.solvedTotal} targets - {Math.round(row.averageScore)}%</small>
                        </li>
                      ))}
                    </ol>
                    {selectedTeacherGroup.ranking.length === 0 ? <p className="fd-table-sub">Nog geen rankingdata.</p> : null}
                  </section>

                  <section>
                    <h4>Co-docenten</h4>
                    <div className="fd-v2-inline-form">
                      <select value={teacherToAdd} onChange={(event) => setTeacherToAdd(event.target.value)}>
                        <option value="">Kies docent</option>
                        {selectedTeacherGroup.availableTeachers.map((teacher) => (
                          <option key={teacher.id} value={teacher.id}>{teacher.name} ({teacher.email})</option>
                        ))}
                      </select>
                      <button className="fd-app-btn" type="button" onClick={() => void addTeacherToGroup()} disabled={!teacherToAdd || teacherGroupBusy}>Toevoegen</button>
                    </div>
                    <div className="fd-v2-chip-list">
                      {selectedTeacherGroup.teachers.map((teacher) => (
                        <span className="chip" key={teacher.id}>
                          {teacher.name}
                          <button type="button" onClick={() => void removeTeacherFromGroup(teacher.id)} disabled={teacherGroupBusy}>x</button>
                        </span>
                      ))}
                    </div>
                  </section>
                </div>

                <div className="fd-v2-group-columns">
                  <section>
                    <h4>Publicaties</h4>
                    <div className="fd-v2-attempt-list">
                      {selectedTeacherGroup.publications.slice(0, 5).map((publication) => (
                        <article className="fd-v2-attempt-card" key={publication.id}>
                          <div>
                            <strong>{publication.ticketTitle}</strong>
                            <p>{publication.attemptCount} attempts, {publication.completedAttemptCount} afgerond - sinds {publication.availableFrom}</p>
                          </div>
                          <span className="fd-status-badge">{publication.status}</span>
                        </article>
                      ))}
                    </div>
                    {selectedTeacherGroup.publications.length === 0 ? <p className="fd-table-sub">Nog geen publicaties voor deze groep.</p> : null}
                  </section>

                  <section>
                    <h4>Gesloten tickets</h4>
                    <div className="fd-v2-attempt-list">
                      {selectedTeacherGroup.closedAttempts.map((attempt) => (
                        <article className="fd-v2-attempt-card" key={attempt.attemptId}>
                          <div>
                            <strong>Ticket {attempt.ticketNumber} - {attempt.ticketTitle}</strong>
                            <p>{attempt.studentName} - {attempt.completedTargets}/{attempt.totalTargets} targets</p>
                          </div>
                          <span className="fd-status-badge">{attempt.status}</span>
                        </article>
                      ))}
                    </div>
                    {selectedTeacherGroup.closedAttempts.length === 0 ? <p className="fd-table-sub">Nog geen gesloten tickets.</p> : null}
                  </section>
                </div>

                <div className="fd-app-table-wrap fd-v2-nested-table">
                  <div className="fd-section-head">
                    <h4>Studenten</h4>
                    <span className="fd-status-badge">{selectedTeacherGroup.memberTotal} leden</span>
                  </div>
                  <table className="fd-app-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        <th>Laatst actief</th>
                        <th className="fd-col-center">Attempts</th>
                        <th className="fd-col-center">Open</th>
                        <th className="fd-col-center">Targets</th>
                        <th className="fd-col-center">Score</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedTeacherGroup.members.map((member) => (
                        <tr
                          className={selectedGroupStudent?.studentId === member.studentId ? "fd-v2-selected-row" : ""}
                          key={member.studentId}
                          onClick={() => setSelectedGroupStudentId(member.studentId)}
                        >
                          <td>
                            <p className="fd-table-name">{member.studentName}</p>
                            <p className="fd-table-sub">{member.email}</p>
                          </td>
                          <td>{member.lastActivity || "-"}</td>
                          <td className="fd-col-center">{member.attemptCount}</td>
                          <td className="fd-col-center">{member.openAttemptCount}</td>
                          <td className="fd-col-center">{member.completedTargets}/{member.totalTargets}</td>
                          <td className="fd-col-center">{Math.round(member.averageScore)}%</td>
                          <td className="fd-col-right">
                            <button className="fd-table-action fd-table-action--secondary" type="button" onClick={() => setSelectedGroupStudentId(member.studentId)}>
                              <Eye aria-hidden="true" size={14} />
                              Voortgang
                            </button>
                            <button className="fd-table-action fd-table-action--secondary" type="button" onClick={(event) => { event.stopPropagation(); void resetStudentPassword(member.studentId); }} disabled={teacherGroupBusy}>
                              <KeyRound aria-hidden="true" size={14} />
                              Reset wachtwoord
                            </button>
                            <button className="fd-table-action fd-table-action--secondary" type="button" onClick={(event) => { event.stopPropagation(); void removeStudentFromGroup(member.studentId); }} disabled={teacherGroupBusy}>
                              <Trash2 aria-hidden="true" size={14} />
                              Verwijder
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {selectedTeacherGroup.members.length === 0 ? <p className="fd-table-sub">Nog geen studenten met attempts in deze groep.</p> : null}
                  <div className="fd-v2-pagination">
                    <button className="fd-app-btn fd-app-btn--outline" type="button" onClick={() => setTeacherGroupMemberPage((page) => Math.max(1, page - 1))} disabled={teacherGroupMemberPage <= 1}>
                      Vorige
                    </button>
                    <span>Pagina {selectedTeacherGroup.memberPage} van {Math.max(1, Math.ceil(selectedTeacherGroup.memberTotal / selectedTeacherGroup.memberPageSize))}</span>
                    <button
                      className="fd-app-btn fd-app-btn--outline"
                      type="button"
                      onClick={() => setTeacherGroupMemberPage((page) => page + 1)}
                      disabled={selectedTeacherGroup.memberPage >= Math.ceil(selectedTeacherGroup.memberTotal / selectedTeacherGroup.memberPageSize)}
                    >
                      Volgende
                    </button>
                  </div>
                </div>

                {selectedGroupStudent ? (
                  <section className="fd-v2-student-progress-card" aria-label={`Voortgang ${selectedGroupStudent.studentName}`}>
                    <div className="fd-v2-row-between">
                      <div>
                        <span className="fd-v2-create-eyebrow">Studentvoortgang</span>
                        <h4>{selectedGroupStudent.studentName}</h4>
                        <p>{selectedGroupStudent.email}</p>
                      </div>
                      <div className="fd-v2-student-progress-stats">
                        <span><strong>{selectedGroupStudent.attemptCount}</strong> attempts</span>
                        <span><strong>{selectedGroupStudent.completedTargets}/{selectedGroupStudent.totalTargets}</strong> targets</span>
                        <span><strong>{Math.round(selectedGroupStudent.averageScore)}%</strong> score</span>
                      </div>
                    </div>
                    <div className="fd-v2-progress-bar" aria-label={`Voortgang ${Math.round(selectedGroupStudent.averageScore)} procent`}>
                      <span style={{ width: `${Math.max(0, Math.min(100, Math.round(selectedGroupStudent.averageScore)))}%` }} />
                    </div>
                    <div className="fd-v2-attempt-list">
                      {selectedGroupStudentAttempts.map((attempt) => (
                        <article className="fd-v2-attempt-card" key={attempt.attemptId}>
                          <div>
                            <strong>Ticket {attempt.ticketNumber} - {attempt.ticketTitle}</strong>
                            <p>{attempt.completedTargets}/{attempt.totalTargets} targets - {attempt.lastActivity || "geen activiteit"}</p>
                          </div>
                          <span className="fd-status-badge">{attempt.status}</span>
                          <button className="fd-table-action" type="button" onClick={() => void handleEvaluateGroupAttempt(attempt)} disabled={evaluatingAttemptId === attempt.attemptId}>
                            <TicketCheck aria-hidden="true" size={14} />
                            {evaluatingAttemptId === attempt.attemptId ? "Evalueren..." : "Evalueer"}
                          </button>
                        </article>
                      ))}
                    </div>
                    {selectedGroupStudentAttempts.length === 0 ? <p className="fd-table-sub">Nog geen attempts voor deze student in deze groep.</p> : null}
                  </section>
                ) : null}
              </>
            ) : (
              <p className="fd-table-sub">Selecteer een groep om leden, ranking en docenten te bekijken.</p>
            )}
          </div>
        </div>
      </section>

      <section className="fd-bento-stat fd-bento-stat--white fd-v2-content-editor">
        <div className="fd-section-head">
          <h2 className="fd-section-title">
            <LockKeyhole aria-hidden="true" size={19} />
            Docentinstellingen
          </h2>
          <span className="fd-status-badge">{teacherTimezone}</span>
        </div>
        <div className="fd-v2-group-filters">
          <label>
            Max actieve tickets
            <input type="number" min={1} max={500} value={teacherMaxActiveTickets} onChange={(event) => setTeacherMaxActiveTickets(event.target.value)} />
          </label>
          <label>
            Timezone
            <input value={teacherTimezone} onChange={(event) => setTeacherTimezone(event.target.value)} />
          </label>
          <label>
            Nieuwe tickets accepteren
            <select value={teacherAcceptsNewTickets ? "yes" : "no"} onChange={(event) => setTeacherAcceptsNewTickets(event.target.value === "yes")}>
              <option value="yes">Ja</option>
              <option value="no">Nee</option>
            </select>
          </label>
        </div>
        <div className="fd-v2-group-filters">
          <label>
            Beschikbaar venster (CSV: dag,start,eind)
            <textarea
              rows={4}
              value={teacherAvailabilityWindows.map((row) => `${row.dayOfWeek},${row.startTime},${row.endTime}`).join("\n")}
              onChange={(event) => {
                const rows = event.target.value.split("\n").map((line) => line.trim()).filter(Boolean);
                setTeacherAvailabilityWindows(rows.map((line) => {
                  const [day, start, end] = line.split(",");
                  return {
                    dayOfWeek: Number(day ?? 0),
                    startTime: (start ?? "09:00").trim(),
                    endTime: (end ?? "17:00").trim()
                  };
                }));
              }}
            />
          </label>
          <label>
            Uitzonderingen (CSV: datum,reden,blocked)
            <textarea
              rows={4}
              value={teacherAvailabilityExceptions.map((row) => `${row.date},${row.reason},${row.blocked ? "1" : "0"}`).join("\n")}
              onChange={(event) => {
                const rows = event.target.value.split("\n").map((line) => line.trim()).filter(Boolean);
                setTeacherAvailabilityExceptions(rows.map((line) => {
                  const [date, reason, blocked] = line.split(",");
                  return {
                    date: (date ?? "").trim(),
                    reason: (reason ?? "").trim(),
                    blocked: (blocked ?? "1").trim() !== "0"
                  };
                }));
              }}
            />
          </label>
        </div>
        <div className="fd-builder-actions">
          <button className="fd-table-action" type="button" onClick={() => void saveTeacherSettings()} disabled={teacherSettingsLoading}>
            {teacherSettingsLoading ? "Opslaan..." : "Instellingen opslaan"}
          </button>
          {teacherSettingsMessage ? <p className="fd-table-sub">{teacherSettingsMessage}</p> : null}
        </div>
      </section>

      <section className="fd-bento-stat fd-bento-stat--white fd-v2-content-editor" id="teacher-settings">
        <div className="fd-section-head">
          <h2 className="fd-section-title">
            <GraduationCap aria-hidden="true" size={19} />
            Klassen
          </h2>
          <span className="fd-status-badge fd-status-badge--active">{classes.length} groepen</span>
        </div>
        <div className="fd-app-table-wrap">
          <table className="fd-app-table">
            <thead>
              <tr>
                <th>Klas</th>
                <th className="fd-col-center">Studenten</th>
                <th className="fd-col-center">Publicaties</th>
                <th className="fd-col-center">Actief</th>
              </tr>
            </thead>
            <tbody>
              {classes.map((classView) => (
                <tr
                  data-class-group-id={classView.classGroupId}
                  key={classView.classGroupId}
                  onClick={() => setSelectedClassId(classView.classGroupId)}
                  style={{ cursor: "pointer", background: classView.classGroupId === selectedClass?.classGroupId ? "rgba(24, 119, 242, 0.08)" : undefined }}
                >
                  <td>
                    <p className="fd-table-text">{classView.className}</p>
                    <p className="fd-table-sub">{classView.classCode}</p>
                  </td>
                  <td className="fd-col-center"><span className="fd-table-score">{classView.studentCount}</span></td>
                  <td className="fd-col-center"><span className="fd-table-score">{classView.publicationCount}</span></td>
                  <td className="fd-col-center"><span className="fd-table-score">{classView.activePublicationCount}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {selectedClass ? (
          <div className="fd-v2-student-attempts">
            <div className="fd-section-head">
              <h3 className="fd-section-title">Studenten in {selectedClass.className}</h3>
            </div>
            <div className="fd-app-table-wrap">
              <table className="fd-app-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Ticket</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedClass.attempts.map((attempt) => (
                    <tr key={`${selectedClass.classGroupId}-${attempt.attemptId}`}>
                      <td>{attempt.studentName}</td>
                      <td>
                        <p className="fd-table-text">Ticket {attempt.ticketNumber}</p>
                        <p className="fd-table-sub">{attempt.ticketTitle}</p>
                      </td>
                      <td><span className="fd-status-badge fd-status-badge--online">{attempt.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </section>

      <section className="fd-bento-stat fd-bento-stat--white fd-v2-content-editor">
        <div className="fd-section-head">
          <h2 className="fd-section-title">
            <FilePenLine aria-hidden="true" size={19} />
            Game ticket builder
          </h2>
          <span className="fd-status-badge fd-status-badge--active">Concept</span>
        </div>
        <div className="draft-stack">
          <label>
            Titel
            <input value={template.title} readOnly />
          </label>
          <label>
            Ticketverhaal
            <textarea value={template.description} readOnly />
          </label>
          <div className="taxonomy-grid">
            <span>Topic <strong>{template.topic}</strong></span>
            <span>Complexiteit <strong>{template.complexity}</strong></span>
            <span>Omgeving <strong>{template.environment}</strong></span>
          </div>
        </div>
      </section>
      {builderOpen ? (
        <WebsiteModal title={`Nieuw game-ticket (${builderStep}/3)`} onClose={() => setBuilderOpen(false)} width={1080}>
          <div className="fd-builder-shell">
            <aside className="fd-builder-steps" aria-label="Game-ticket stappen">
              {[
                ["1", "Template", "Verhaal, topic en leerdoelen"],
                ["2", "Targets", "Bewijs, scoring en expected JSON"],
                ["3", "Publiceren", "Klas, periode en feedback"]
              ].map(([number, title, subtitle], index) => (
                <button
                  className={`fd-builder-step ${builderStep === index + 1 ? "is-active" : ""} ${builderStep > index + 1 ? "is-done" : ""}`}
                  disabled={index + 1 > builderStep}
                  key={number}
                  onClick={() => setBuilderStep((index + 1) as 1 | 2 | 3)}
                  type="button"
                >
                  <span>{number}</span>
                  <strong>{title}</strong>
                  <small>{subtitle}</small>
                </button>
              ))}
            </aside>
            <div className="fd-builder-main">
              <div className="fd-v2-row-between">
                <div>
                  <h3 className="fd-section-title">Nieuw game-ticket</h3>
                  <p className="fd-table-sub">Maak een DB-backed tickettemplate, voeg targets toe en publiceer naar een klas.</p>
                </div>
                <button type="button" className="fd-table-action fd-table-action--secondary" onClick={() => setBuilderOpen(false)}>Sluiten</button>
              </div>
              {builderError ? <p className="fd-builder-alert">{builderError}</p> : null}
            {builderStep === 1 ? (
              <div className="fd-builder-panel">
                <label className="fd-builder-field fd-builder-field--wide">Titel<input value={templateDraft.title} onChange={(event) => setTemplateDraft({ ...templateDraft, title: event.target.value })} placeholder="Bijv. DNS storing bij intakebalie" /></label>
                <label className="fd-builder-field fd-builder-field--wide">Beschrijving<textarea value={templateDraft.description} onChange={(event) => setTemplateDraft({ ...templateDraft, description: event.target.value })} placeholder="Beschrijf de melding, context en gewenste eindsituatie." /></label>
                <label className="fd-builder-field">Topic
                  <select value={templateDraft.topic} onChange={(event) => setTemplateDraft({ ...templateDraft, topic: event.target.value })}>
                    {topicOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </label>
                <label className="fd-builder-field">Complexiteit
                  <select value={templateDraft.complexity} onChange={(event) => setTemplateDraft({ ...templateDraft, complexity: event.target.value })}>
                    {complexityOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </label>
                <label className="fd-builder-field">Omgeving
                  <select value={templateDraft.environment} onChange={(event) => setTemplateDraft({ ...templateDraft, environment: event.target.value })}>
                    {environmentOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                  </select>
                </label>
                <label className="fd-builder-field fd-builder-field--wide">Skills (komma gescheiden)<input value={templateDraft.skills} onChange={(event) => setTemplateDraft({ ...templateDraft, skills: event.target.value })} placeholder="DNS, Active Directory, troubleshooting" /></label>
                <label className="fd-builder-field fd-builder-field--wide">Leerdoelen (1 per regel)<textarea value={templateDraft.learningObjectives} onChange={(event) => setTemplateDraft({ ...templateDraft, learningObjectives: event.target.value })} /></label>
                <div className="fd-builder-actions">
                  <button type="button" className="fd-table-action" onClick={() => void createTemplateStep()} disabled={!templateDraft.title.trim() || !templateDraft.description.trim() || builderBusy}>{builderBusy ? "Opslaan..." : "Template opslaan en naar targets"}</button>
                </div>
              </div>
            ) : null}
            {builderStep === 2 ? (
              <div className="fd-builder-targets">
                {targetsDraft.map((target, index) => (
                  <article className="fd-builder-target-card" key={target.id}>
                    <div className="fd-v2-row-between">
                      <h4 className="fd-announce-title">Target {index + 1}</h4>
                      <button className="fd-table-action fd-table-action--secondary" disabled={targetsDraft.length <= 1 || builderBusy} onClick={() => removeTargetDraft(index)} type="button">Verwijderen</button>
                    </div>
                    <label className="fd-builder-field fd-builder-field--wide">Titel<input value={target.title} onChange={(event) => updateTargetDraft(index, { title: event.target.value })} placeholder="Wat moet de student aantonen?" /></label>
                    <label className="fd-builder-field fd-builder-field--wide">Safe title<input value={target.safeTitle} onChange={(event) => updateTargetDraft(index, { safeTitle: event.target.value })} placeholder="Studentveilige titel voor verborgen targets" /></label>
                    <label className="fd-builder-field fd-builder-field--wide">Beschrijving<textarea value={target.description} onChange={(event) => updateTargetDraft(index, { description: event.target.value })} /></label>
                    <label className="fd-builder-field">Evaluator key
                      <select value={target.evaluatorKey} onChange={(event) => updateTargetDraft(index, { evaluatorKey: event.target.value })}>
                        {evaluatorOptions.map((option) => <option key={option.value} value={option.value}>{option.label} ({option.value})</option>)}
                      </select>
                    </label>
                    <label className="fd-builder-field">Visibility
                      <select value={target.visibility} onChange={(event) => updateTargetDraft(index, { visibility: event.target.value as typeof target.visibility })}>
                        <option value="visible">visible</option>
                        <option value="hidden">hidden</option>
                        <option value="hidden_until_completed">hidden_until_completed</option>
                      </select>
                    </label>
                    <label className="fd-builder-field">Scoring
                      <select value={target.scoring} onChange={(event) => updateTargetDraft(index, { scoring: event.target.value as typeof target.scoring })}>
                        <option value="automatic">automatic</option>
                        <option value="manual">manual</option>
                        <option value="hybrid">hybrid</option>
                      </select>
                    </label>
                    <label className="fd-builder-field">Weight<input min="0" step="0.1" type="number" value={target.weight} onChange={(event) => updateTargetDraft(index, { weight: event.target.value })} /></label>
                    <label className="fd-builder-field fd-builder-field--wide">Expected JSON<textarea className="fd-builder-code" value={target.expected} onChange={(event) => updateTargetDraft(index, { expected: event.target.value })} spellCheck={false} /></label>
                    <label className="fd-builder-field fd-builder-field--wide">Docentnotities<textarea value={target.teacherNotes} onChange={(event) => updateTargetDraft(index, { teacherNotes: event.target.value })} /></label>
                  </article>
                ))}
                <div className="fd-builder-actions">
                  <button type="button" className="fd-table-action fd-table-action--secondary" onClick={() => setBuilderStep(1)} disabled={builderBusy}>Terug</button>
                  <button type="button" className="fd-table-action fd-table-action--secondary" onClick={addTargetDraft} disabled={builderBusy}>Target toevoegen</button>
                  <button type="button" className="fd-table-action" onClick={() => void publishVersionStep()} disabled={targetsDraft.some((target) => !target.title.trim()) || builderBusy}>{builderBusy ? "Publiceren..." : "Versie publiceren"}</button>
                </div>
              </div>
            ) : null}
            {builderStep === 3 ? (
              <div className="fd-builder-panel">
                <label className="fd-builder-field fd-builder-field--wide">Klas
                  <select value={publicationDraft.classGroupId} onChange={(event) => setPublicationDraft({ ...publicationDraft, classGroupId: event.target.value })}>
                    {classes.map((classView) => <option key={classView.classGroupId} value={classView.classGroupId}>{classView.className}</option>)}
                  </select>
                </label>
                <label className="fd-builder-field">Beschikbaar vanaf<input type="datetime-local" value={publicationDraft.availableFrom} onChange={(event) => setPublicationDraft({ ...publicationDraft, availableFrom: event.target.value })} /></label>
                <label className="fd-builder-field">Beschikbaar tot<input type="datetime-local" value={publicationDraft.availableUntil} onChange={(event) => setPublicationDraft({ ...publicationDraft, availableUntil: event.target.value })} /></label>
                <label className="fd-builder-field">Attempt limit<input min="1" type="number" value={publicationDraft.attemptLimit} onChange={(event) => setPublicationDraft({ ...publicationDraft, attemptLimit: event.target.value })} /></label>
                <label className="fd-builder-field">Feedback mode
                  <select value={publicationDraft.feedbackMode} onChange={(event) => setPublicationDraft({ ...publicationDraft, feedbackMode: event.target.value })}>
                    <option value="immediate">immediate</option>
                    <option value="after_submission">after_submission</option>
                    <option value="teacher_released">teacher_released</option>
                    <option value="hidden">hidden</option>
                  </select>
                </label>
                <div className="fd-builder-preview">
                  <strong>{templateDraft.title}</strong>
                  <span>{targetsDraft.length} target(s) - {classes.find((classView) => classView.classGroupId === publicationDraft.classGroupId)?.className ?? "geen klas"}</span>
                </div>
                <div className="fd-builder-actions">
                  <button type="button" className="fd-table-action fd-table-action--secondary" onClick={() => setBuilderStep(2)} disabled={builderBusy}>Terug</button>
                  <button type="button" className="fd-table-action" onClick={() => void publishClassStep()} disabled={builderBusy || !publicationDraft.classGroupId}>{builderBusy ? "Publiceren..." : "Publiceren naar klas"}</button>
                </div>
              </div>
            ) : null}
            </div>
          </div>
        </WebsiteModal>
      ) : null}
    </>
  );
}
